import { useState, useEffect, useMemo } from 'react';
import { generateVideoThumbnailFromUrl } from 'utils/generateVideoThumbnail';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from 'utils/firebase';
import { updateMediaEntryThumbnail } from 'utils/services/fileCabinetService';

interface VideoEntry {
  id: string;
  url: string;
  fileCabinetId?: number;
}

interface BackfillContext {
  projectId: string | number;
  companyId: string | number;
}

const MAX_CACHE_SIZE = 100;
const MAX_GENERATION_ATTEMPTS = 3;
const RETRY_DELAY_MS = 2000;
const thumbnailCache = new Map<string, string>();
const backfillAttempted = new Set<number>();

export const useVideoThumbnails = (
  videos: VideoEntry[],
  backfillContext?: BackfillContext,
): Map<string, string> => {
  const [thumbnails, setThumbnails] = useState<Map<string, string>>(() => {
    const initial = new Map<string, string>();
    videos.forEach(({ id }) => {
      const cached = thumbnailCache.get(id);
      if (cached) initial.set(id, cached);
    });
    return initial;
  });

  const videoKey = useMemo(
    () => videos.map((v) => `${v.id}:${v.url}:${v.fileCabinetId ?? ''}`).join('|'),
    [videos]
  );

  useEffect(() => {
    if (!videos.length) return;

    let cancelled = false;
    const retryTimers: ReturnType<typeof setTimeout>[] = [];

    const tryGenerate = (
      id: string,
      url: string,
      fileCabinetId: number | undefined,
      attempt: number,
    ): void => {
      generateVideoThumbnailFromUrl(url)
        .then((dataUrl) => {
          if (cancelled) return;
          if (thumbnailCache.size >= MAX_CACHE_SIZE) {
            const firstKey = thumbnailCache.keys().next().value;
            if (firstKey) thumbnailCache.delete(firstKey);
          }
          thumbnailCache.set(id, dataUrl);
          setThumbnails((prev) => {
            const updated = new Map(prev);
            updated.set(id, dataUrl);
            return updated;
          });

          if (
            backfillContext &&
            fileCabinetId &&
            fileCabinetId > 0 &&
            !backfillAttempted.has(fileCabinetId)
          ) {
            backfillAttempted.add(fileCabinetId);
            void (async () => {
              try {
                const thumbBlob = await (await fetch(dataUrl)).blob();
                const thumbStoragePath = `companies/${backfillContext.companyId}/projects/${backfillContext.projectId}/lazy-backfill/${fileCabinetId}.thumb.jpg`;
                const thumbRef = storageRef(storage, thumbStoragePath);
                await uploadBytes(thumbRef, thumbBlob);
                const thumbDownloadUrl = await getDownloadURL(thumbRef);
                await updateMediaEntryThumbnail(
                  backfillContext.projectId,
                  fileCabinetId,
                  thumbDownloadUrl,
                );
              } catch (err) {
                backfillAttempted.delete(fileCabinetId);
                console.warn('[useVideoThumbnails] Lazy backfill failed (non-fatal):', err);
              }
            })();
          }
        })
        .catch(() => {
          if (cancelled || attempt >= MAX_GENERATION_ATTEMPTS) return;
          const timer = setTimeout(() => {
            if (!cancelled) tryGenerate(id, url, fileCabinetId, attempt + 1);
          }, RETRY_DELAY_MS);
          retryTimers.push(timer);
        });
    };

    videos.forEach(({ id, url, fileCabinetId }) => {
      if (!url) return;

      if (thumbnailCache.has(id)) {
        setThumbnails((prev) => {
          if (prev.has(id)) return prev;
          const updated = new Map(prev);
          updated.set(id, thumbnailCache.get(id)!);
          return updated;
        });
        return;
      }

      tryGenerate(id, url, fileCabinetId, 1);
    });

    return () => {
      cancelled = true;
      retryTimers.forEach((t) => clearTimeout(t));
    };
  }, [videoKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return thumbnails;
};
